import React, { useEffect, useState } from "react";
import { api, errMsg } from "../api";
import { Gift, Upload, Share2, Sparkles, Check, X, Instagram, Facebook } from "lucide-react";

export default function ShareToWin() {
    const [campaigns, setCampaigns] = useState([]);
    const [active, setActive] = useState(null);

    useEffect(() => {
        api.get("/coupons/social-campaigns").then((r) => setCampaigns(r.data || [])).catch(() => {});
    }, []);

    const reload = () => api.get("/coupons/social-campaigns").then((r) => setCampaigns(r.data || []));

    return (
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16" data-testid="share-to-win-page">
            <div className="text-center mb-10">
                <p className="eyebrow flex items-center gap-2 justify-center"><Sparkles size={12} /> Share & Earn</p>
                <h1 className="h-display text-[34px] sm:text-[48px] lg:text-[56px] mt-3 leading-[1.05]">
                    Share us, win a coupon.
                </h1>
                <p className="text-[14px] sm:text-[15px] text-[var(--body)] max-w-xl mx-auto mt-4">
                    Post about UCare on Instagram, Facebook, or X — upload the screenshot, and the first few winners
                    receive an exclusive promo code.
                </p>
            </div>

            {campaigns.length === 0 ? (
                <div className="card-glass p-12 text-center" data-testid="share-to-win-empty">
                    <Gift size={36} className="text-[var(--taupe)] mx-auto mb-4" />
                    <h3 className="font-serif text-2xl text-[var(--ink)] mb-2">No active campaigns right now</h3>
                    <p className="text-[14px] text-[var(--ink-soft)]">Check back soon — we run share-to-win drops every few weeks.</p>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
                    {campaigns.map((c) => (
                        <div
                            key={c.code}
                            className="card-glass p-6 sm:p-8 hover-float"
                            data-testid={`campaign-card-${c.code}`}
                        >
                            <div className="flex items-start gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-[var(--bg-alt)] grid place-items-center text-[var(--taupe)] shrink-0">
                                    <Gift size={18} />
                                </div>
                                <div className="flex-1">
                                    <div className="font-mono text-[18px] font-bold text-[var(--ink)] tracking-wider">{c.code}</div>
                                    <div className="text-[13px] text-[var(--body)]">
                                        {c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`} for the first {c.max_winners} winner{c.max_winners > 1 ? "s" : ""}.
                                    </div>
                                </div>
                            </div>
                            {c.social_share_message && (
                                <p className="text-[13px] text-[var(--body)] mb-4 leading-relaxed">{c.social_share_message}</p>
                            )}
                            <div className="flex items-center justify-between text-[12px] text-[var(--body)] mb-5">
                                <span data-testid={`campaign-slots-${c.code}`}>
                                    {c.winners_count}/{c.max_winners} slot{c.max_winners > 1 ? "s" : ""} claimed
                                </span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.16em] ${c.is_open ? "bg-[#e3f4d9] text-[#3b7a36]" : "bg-[#fde6d6] text-[#a4521f]"}`}>
                                    {c.is_open ? "Open" : "Full"}
                                </span>
                            </div>
                            <button
                                onClick={() => setActive(c)}
                                disabled={!c.is_open}
                                className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid={`claim-coupon-btn-${c.code}`}
                            >
                                {c.is_open ? <><Share2 size={14} /> Submit my share</> : "All slots claimed"}
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {active && (
                <ClaimModal
                    campaign={active}
                    onClose={() => { setActive(null); reload(); }}
                />
            )}
        </div>
    );
}

function ClaimModal({ campaign, onClose }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [platform, setPlatform] = useState("instagram");
    const [uploading, setUploading] = useState(false);
    const [screenshot, setScreenshot] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setErr(""); setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/screenshot", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setScreenshot(data.url);
        } catch (ex) {
            setErr(errMsg(ex));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!screenshot) { setErr("Please upload the screenshot of your post."); return; }
        setErr(""); setSubmitting(true);
        try {
            await api.post("/coupons/claim", {
                coupon_code: campaign.code,
                name, email,
                social_platform: platform,
                screenshot_url: screenshot,
            });
            setDone(true);
        } catch (ex) {
            setErr(errMsg(ex));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="drawer-backdrop" onClick={onClose} data-testid="claim-modal-backdrop">
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white max-w-[520px] mx-auto my-10 rounded-2xl overflow-hidden shadow-xl"
                data-testid={`claim-modal-${campaign.code}`}
            >
                <div className="p-6 sm:p-8">
                    <div className="flex items-start justify-between mb-5">
                        <div>
                            <div className="overline mb-1">Share-to-win</div>
                            <h3 className="font-serif text-2xl text-[var(--ink)]">{campaign.code}</h3>
                        </div>
                        <button onClick={onClose} className="text-[var(--body)] hover:text-[var(--ink)] p-1" data-testid="close-claim-modal">
                            <X size={18} />
                        </button>
                    </div>
                    {done ? (
                        <div className="text-center py-6" data-testid="claim-success">
                            <div className="w-14 h-14 rounded-full bg-[#e3f4d9] grid place-items-center mx-auto mb-4 text-[#3b7a36]">
                                <Check size={26} />
                            </div>
                            <h4 className="font-serif text-xl text-[var(--ink)] mb-2">Submitted!</h4>
                            <p className="text-[13.5px] text-[var(--body)] mb-5">
                                We'll review your screenshot. If you're one of the first {campaign.max_winners} winner{campaign.max_winners > 1 ? "s" : ""},
                                we'll email <b>{email}</b> with the activated coupon.
                            </p>
                            <button onClick={onClose} className="btn btn-primary" data-testid="claim-done-close">
                                Done
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <p className="text-[13px] text-[var(--body)] leading-relaxed">
                                Post about us, then upload the screenshot below. <b>{campaign.slots_left}</b> slot{campaign.slots_left !== 1 ? "s" : ""} left.
                            </p>
                            <div>
                                <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--taupe)] block mb-1">Your name</label>
                                <input
                                    className="w-full border border-[var(--line)] rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:border-[var(--taupe)]"
                                    value={name} onChange={(e) => setName(e.target.value)}
                                    required placeholder="Jane Doe"
                                    data-testid="claim-name"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--taupe)] block mb-1">Email</label>
                                <input
                                    type="email"
                                    className="w-full border border-[var(--line)] rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:border-[var(--taupe)]"
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    required placeholder="you@example.com"
                                    data-testid="claim-email"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--taupe)] block mb-1">Platform</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { id: "instagram", label: "Instagram", Icon: Instagram },
                                        { id: "facebook", label: "Facebook", Icon: Facebook },
                                        { id: "twitter", label: "X / Twitter", Icon: Share2 },
                                    ].map((p) => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => setPlatform(p.id)}
                                            className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[12px] border transition ${
                                                platform === p.id
                                                    ? "bg-[var(--taupe)] text-white border-[var(--taupe)]"
                                                    : "border-[var(--line)] text-[var(--body)] hover:border-[var(--taupe)]"
                                            }`}
                                            data-testid={`claim-platform-${p.id}`}
                                        >
                                            <p.Icon size={14} /> {p.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--taupe)] block mb-1">Screenshot</label>
                                {screenshot ? (
                                    <div className="relative inline-block">
                                        <img src={screenshot} alt="" className="w-32 h-32 object-cover rounded-lg border border-[var(--line)]" data-testid="claim-screenshot-preview" />
                                        <button
                                            type="button"
                                            onClick={() => setScreenshot("")}
                                            className="absolute -top-1 -right-1 bg-[var(--terra)] text-white rounded-full w-5 h-5 grid place-items-center text-xs"
                                            data-testid="claim-screenshot-remove"
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                ) : (
                                    <label className={`flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-lg border border-dashed cursor-pointer ${uploading ? "opacity-60" : "hover:border-[var(--taupe)]"} border-[var(--line)]`} data-testid="claim-upload-drop">
                                        <Upload size={18} className="text-[var(--body)]" />
                                        <span className="text-[12px] text-[var(--body)]">{uploading ? "Uploading…" : "Click to upload (max 8MB)"}</span>
                                        <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} data-testid="claim-upload-input" />
                                    </label>
                                )}
                            </div>
                            {err && <div className="text-[var(--terra)] text-[13px]" data-testid="claim-error">{err}</div>}
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={onClose} className="btn btn-outline flex-1">Cancel</button>
                                <button disabled={submitting || uploading || !screenshot} className="btn btn-primary flex-1 disabled:opacity-50" data-testid="claim-submit">
                                    {submitting ? "Submitting…" : "Submit claim"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
